import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import LegalPage from '../components/ui/LegalPage';
import DataTable from '../components/ui/DataTable';
import Notice from '../components/ui/Notice';
import DlFacts from '../components/ui/DlFacts';
import { entity } from '../content/en';

const Privacy = () => {
  const { t } = useTranslation('cy');
  const meta = t('privacy.meta', { returnObjects: true });

  const sections = [
    { id: 'privacy-collection', title: t('privacy.collection.h2') },
    { id: 'privacy-use', title: t('privacy.use.h2') },
    { id: 'privacy-disclosure', title: t('privacy.disclosure.h2') },
    { id: 'privacy-retention-security', title: t('privacy.retention.h2') },
    { id: 'privacy-rights', title: t('privacy.rights.h2') },
    { id: 'privacy-cookies-links', title: t('privacy.cookies.h2') },
    { id: 'privacy-contact', title: t('privacy.contact.h2') },
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/privacy" />

      <LegalPage
        eyebrow={t('privacy.header.eyebrow')}
        title={t('privacy.header.h1')}
        metaBlock={
          <DlFacts
            rows={[
              { label: 'Controller', value: entity.name },
              { label: 'Updated', value: t('privacy.header.updated') },
            ]}
          />
        }
        summary={
          <>
            {t('privacy.header.summary')}{' '}
            <strong style={{ color: 'var(--cy-color-warning)' }}>{t('privacy.header.notice')}</strong>
          </>
        }
        sections={sections}
      >
        <p style={{ fontSize: 'var(--cy-size-body)', lineHeight: 1.75, color: 'var(--cy-color-text-secondary)' }}>
          {t('privacy.header.scope')}
        </p>

        <section id="privacy-collection">
          <h2>{t('privacy.collection.h2')}</h2>
          <DataTable
            columns={[
              { key: 'scenario', label: 'Scenario' },
              { key: 'data', label: 'Data categories' },
              { key: 'source', label: 'Source' },
              { key: 'required', label: 'Required' },
            ]}
            rows={t('privacy.collection.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.collection.sensitive')}</p>
        </section>

        <section id="privacy-use">
          <h2>{t('privacy.use.h2')}</h2>
          <DataTable
            columns={[
              { key: 'purpose', label: 'Purpose' },
              { key: 'data', label: 'Data' },
              { key: 'basis', label: 'Basis' },
              { key: 'consequence', label: 'If not provided' },
            ]}
            rows={t('privacy.use.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.use.marketing')}</p>
        </section>

        <section id="privacy-disclosure">
          <h2>{t('privacy.disclosure.h2')}</h2>
          <DataTable
            columns={[
              { key: 'recipient', label: 'Recipient' },
              { key: 'purpose', label: 'Purpose' },
              { key: 'data', label: 'Data' },
              { key: 'location', label: 'Location' },
              { key: 'safeguard', label: 'Safeguards' },
            ]}
            rows={t('privacy.disclosure.rows', { returnObjects: true })}
          />
          <Notice variant="inline" style={{ marginTop: 24 }}>{t('privacy.disclosure.note')}</Notice>
        </section>

        <section id="privacy-retention-security">
          <h2>{t('privacy.retention.h2')}</h2>
          <DataTable
            columns={[
              { key: 'type', label: 'Record type' },
              { key: 'term', label: 'Period / criteria' },
            ]}
            rows={t('privacy.retention.rows', { returnObjects: true })}
          />
          <p style={{ marginTop: 24 }}>{t('privacy.retention.security')}</p>
          <p>{t('privacy.retention.incident')}</p>
        </section>

        <section id="privacy-rights">
          <h2>{t('privacy.rights.h2')}</h2>
          <DataTable
            columns={[
              { key: 'right', label: 'Right' },
              { key: 'how', label: 'How to exercise' },
              { key: 'limit', label: 'Limits' },
            ]}
            rows={t('privacy.rights.rows', { returnObjects: true })}
          />
        </section>

        <section id="privacy-cookies-links">
          <h2>{t('privacy.cookies.h2')}</h2>
          <p>{t('privacy.cookies.note')}</p>
          <p>{t('privacy.cookies.external')}</p>
        </section>

        <section id="privacy-contact">
          <h2>{t('privacy.contact.h2')}</h2>
          <p>{t('privacy.contact.enquiries')}</p>
          <p>{t('privacy.contact.complaints')}</p>
          <p>{t('privacy.contact.updates')}</p>
          <p style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/contact">{t('privacy.contact.cta')}</Link>
          </p>
        </section>
      </LegalPage>
    </Layout>
  );
};

export default Privacy;
